import { useState, useEffect, useCallback, useRef } from 'react'
import { getSupabase } from '../utils/supabaseClient'
import Link from 'next/link'
import ProfileButton from '../components/ProfileButton'
import { useRouter } from 'next/router'

export default function Articles() {
  const [articles, setArticles] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [retryCount, setRetryCount] = useState(0)
  const [user, setUser] = useState(null)
  const articlesPerPage = 10
  const MAX_RETRIES = 3
  const LOADING_TIMEOUT = 10000 // 10 detik
  const router = useRouter()
  const supabase = getSupabase();
  
  // Gunakan useRef untuk menyimpan state yang tidak perlu trigger re-render
  const timeoutRef = useRef(null)
  const isMounted = useRef(true)
  const retryTimeoutRef = useRef(null)
  const retryCountRef = useRef(0)

  // Fungsi untuk memeriksa sesi dan mengatur state user
  const checkSession = useCallback(async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (!session) {
        // Coba refresh token
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshData.session) {
          router.push('/login');
          throw new Error('Sesi tidak valid. Silakan login kembali.');
        }
        
        // Simpan data user ke state
        setUser(refreshData.session.user);
        return refreshData.session;
      }
      
      // Simpan data user ke state
      setUser(session.user);
      return session;
    } catch (error) {
      console.error('Error checking session:', error);
      router.push('/login');
      throw error;
    }
  }, [router]);

  const fetchArticles = useCallback(async () => {
    if (!isMounted.current) return;

    try {
      setLoading(true)
      setError(null)
      
      // Bersihkan timeout sebelumnya jika ada
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      
      // Tambahkan timeout untuk loading
      timeoutRef.current = setTimeout(() => {
        if (isMounted.current) {
          setLoading(false)
          setError('Waktu loading terlalu lama. Silakan coba lagi.')
        }
      }, LOADING_TIMEOUT)

      // Cek sesi
      const session = await checkSession()
      
      // Ambil total jumlah artikel untuk paginasi
      const { count, error: countError } = await supabase
        .from('articles')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', session.user.id)

      if (countError) throw countError

      // Hitung total halaman
      const total = count || 0
      const pages = Math.ceil(total / articlesPerPage)
      setTotalPages(pages)

      // Ambil artikel dengan paginasi
      const { data, error: fetchError } = await supabase
        .from('articles')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .range((currentPage - 1) * articlesPerPage, currentPage * articlesPerPage - 1)
      
      if (fetchError) throw fetchError
      
      if (isMounted.current) {
        setArticles(data || [])
        retryCountRef.current = 0 // Reset retry count jika berhasil
        setRetryCount(0)
      }
    } catch (error) {
      console.error('Error fetching articles:', error)
      if (isMounted.current) {
        setError(error.message || 'Gagal memuat artikel')
        
        // Cek apakah perlu retry
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current += 1
          setRetryCount(retryCountRef.current)
        }
      }
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [currentPage, checkSession]) // Hapus retryCount dari dependency array

  // Effect untuk fetch articles
  useEffect(() => {
    isMounted.current = true
    fetchArticles()

    return () => {
      isMounted.current = false
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, [fetchArticles])

  // Fungsi untuk retry dengan delay
  const handleRetry = useCallback(() => {
    if (retryCountRef.current >= MAX_RETRIES) {
      setError('Terlalu banyak percobaan. Silakan refresh halaman.')
      return
    }
    
    // Bersihkan timeout retry sebelumnya jika ada
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current)
    }
    
    // Tambahkan delay sebelum retry
    retryTimeoutRef.current = setTimeout(() => {
      fetchArticles()
    }, 1000 * (retryCountRef.current + 1)) // Delay meningkat setiap retry
  }, [fetchArticles])

  // Fungsi untuk mengganti halaman
  const changePage = useCallback((page) => {
    if (page < 1 || page > totalPages) return
    setCurrentPage(page)
  }, [totalPages])

  // Fungsi untuk menghasilkan array nomor halaman yang akan ditampilkan
  const getPageNumbers = () => {
    const pageNumbers = []
    
    if (totalPages <= 7) {
      // Tampilkan semua halaman jika totalnya <= 7
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i)
      }
    } else {
      // Tampilkan beberapa halaman dengan ... jika totalnya > 7
      if (currentPage <= 3) {
        // Jika halaman aktif di awal, tampilkan 1-5, ..., totalPages
        for (let i = 1; i <= 5; i++) {
          pageNumbers.push(i)
        }
        pageNumbers.push('...')
        pageNumbers.push(totalPages)
      } else if (currentPage >= totalPages - 2) {
        // Jika halaman aktif di akhir, tampilkan 1, ..., totalPages-4 sampai totalPages
        pageNumbers.push(1)
        pageNumbers.push('...')
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pageNumbers.push(i)
        }
      } else {
        // Jika halaman aktif di tengah, tampilkan 1, ..., currentPage-1, currentPage, currentPage+1, ..., totalPages
        pageNumbers.push(1)
        pageNumbers.push('...')
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i)
        }
        pageNumbers.push('...')
        pageNumbers.push(totalPages)
      }
    }
    
    return pageNumbers
  }

  const handleDelete = async (id) => {
    if (!confirm('Apakah Anda yakin ingin menghapus artikel ini?')) return

    try {
      // Ambil user ID dari sesi
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Anda harus login untuk menghapus artikel');
      }
      
      const { error } = await supabase
        .from('articles')
        .delete()
        .eq('id', id)
        .eq('user_id', session.user.id) // Pastikan hanya menghapus artikel milik user ini
      
      if (error) throw error
      
      // Refresh artikel setelah penghapusan
      fetchArticles()
    } catch (error) {
      console.error('Error deleting article:', error)
      alert('Gagal menghapus artikel')
    }
  }

  // Format timestamp
  const formatTimestamp = (isoString) => {
    if (!isoString) return ''
    const date = new Date(isoString)
    return date.toLocaleString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Fungsi untuk membersihkan teks dari markdown dan menampilkan plain text
  const cleanMarkdown = (markdownText) => {
    if (!markdownText) return '';
    
    // Hapus header markdown
    let text = markdownText.replace(/#+\s+(.*?)\n/g, '$1 ');
    
    // Hapus bold, italic, dll
    text = text.replace(/\*\*(.*?)\*\*/g, '$1');
    text = text.replace(/\*(.*?)\*/g, '$1');
    text = text.replace(/~~(.*?)~~/g, '$1');
    text = text.replace(/`(.*?)`/g, '$1');
    
    // Hapus list items
    text = text.replace(/^\s*[-*+]\s+(.*?)$/gm, '$1');
    text = text.replace(/^\s*\d+\.\s+(.*?)$/gm, '$1');
    
    // Hapus links
    text = text.replace(/\[(.*?)\]\(.*?\)/g, '$1');
    
    // Hapus blockquotes
    text = text.replace(/^\s*>\s+(.*?)$/gm, '$1');
    
    return text;
  };

  // Tambahkan fungsi handleNavigation untuk hard refresh
  const handleNavigation = (e, path) => {
    e.preventDefault();
    
    // Periksa apakah ini adalah navigasi ke URL yang sama dengan URL saat ini
    const currentPath = window.location.pathname;
    const targetPath = path.split('?')[0]; // Hapus query params untuk perbandingan
    
    // Jika mencoba navigasi ke URL yang sama, jangan lakukan apa-apa
    if (currentPath === targetPath) {
      console.log('Sudah berada di URL yang sama, navigasi dibatalkan');
      return;
    }
    
    // Khusus untuk navigasi ke detail artikel, berikan penanda khusus
    const isArticleDetail = path.includes('/article/');
    
    // Simpan URL tujuan ke sessionStorage dengan penanda tambahan jika ini halaman detail artikel
    sessionStorage.setItem('nextRoute', path);
    if (isArticleDetail) {
      sessionStorage.setItem('isArticleDetailNavigation', 'true');
    }
    
    // Simpan state pengguna jika perlu
    if (user && user.id) {
      sessionStorage.setItem('preserveUser', JSON.stringify({
        id: user.id,
        timestamp: Date.now()
      }));
    }
    
    // Lakukan hard refresh dengan parameter untuk mencegah cache
    window.location.href = path + (path.includes('?') ? '&' : '?') + '_refresh=' + Date.now();
  };

  return (
    <div>
      {/* Header dengan logo */}
      <header className="app-header">
        <div className="header-container">
          <div className="logo-container">
            <Link href="/">
              <img 
                src="https://ratu.ai/wp-content/uploads/2025/04/penulis-pro-ratu-logo.jpg" 
                alt="Penulis Pro Logo" 
                className="app-logo" 
              />
            </Link>
          </div>
          <div className="right-section">
            <div className="nav-links">
              <a 
                href="/" 
                onClick={(e) => handleNavigation(e, '/')}
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M12 19l7-7 3 3-7 7-3-3z"></path>
                  <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"></path>
                  <path d="M2 2l7.586 7.586"></path>
                  <circle cx="11" cy="11" r="2"></circle>
                </svg>
                <span>Buat Artikel</span>
              </a>
              <a 
                href="/articles" 
                className="nav-link active"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                </svg>
                <span>Artikel Tersimpan</span>
              </a>
              <a 
                href="/humanize" 
                onClick={(e) => handleNavigation(e, '/humanize')}
                className="nav-link"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="nav-icon">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="9" y1="8" x2="15" y2="8"></line>
                  <line x1="9" y1="12" x2="15" y2="12"></line>
                  <line x1="9" y1="16" x2="13" y2="16"></line>
                  <line x1="17" y1="17" x2="19" y2="19"></line>
                  <line x1="19" y1="17" x2="17" y2="19"></line>
                </svg>
                <span>Humanize</span>
              </a>
            </div>
            <ProfileButton />
          </div>
        </div>
      </header>

      <div className="container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Memuat artikel... {retryCount > 0 && `(Percobaan ${retryCount}/${MAX_RETRIES})`}</p>
          </div>
        ) : error ? (
          <div className="error-message">
            <p>{error}</p>
            <button 
              onClick={handleRetry} 
              className="retry-button"
              disabled={retryCount >= MAX_RETRIES}
            >
              {retryCount >= MAX_RETRIES ? 'Terlalu Banyak Percobaan' : 'Coba Lagi'}
            </button>
          </div>
        ) : articles.length === 0 ? (
          <div className="empty-state">
            <p>Belum ada artikel yang disimpan</p>
            <Link href="/" className="create-button">
              Buat Artikel Baru
            </Link>
          </div>
        ) : (
          <>
            <div className="articles-list">
              {articles.map(article => (
                <div key={article.id} className="article-card">
                  <div className="article-header">
                    <h2 className="article-title">{article.title}</h2>
                    <div className="article-meta">
                      <span className="article-type">{article.article_type}</span>
                      <span className="article-language">{article.language}</span>
                      <span className="article-style">{article.writing_style}</span>
                      {article.article_type !== 'Cerita' && (
                        <span className="article-source article-reference-source">
                          <span className="source-label">Sumber:</span> {article.reference_source === 'Indonesia' || !article.reference_source ? 'Indonesia' : 'Luar Negeri'}
                        </span>
                      )}
                    </div>
                    
                    {/* Badge Humanized jika artikel telah dihumanize */}
                    {article.is_humanized && (
                      <div className="humanized-badge-container article-list-badge">
                        <div className="humanized-badge">
                          <span className="humanized-icon">✓</span>
                          <span className="humanized-text">AI-Undetectable</span>
                          <div className="humanized-tooltip">Artikel ini telah dioptimalkan agar tidak terdeteksi sebagai konten AI oleh detector seperti GPTZero, Winston AI, dan lainnya.</div>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="article-content-preview">
                    {cleanMarkdown(article.content).substring(0, 150)}...
                  </div>
                  <div className="article-footer">
                    <span className="article-date">
                      Dibuat pada: {formatTimestamp(article.created_at)}
                    </span>
                    <div className="article-actions">
                      <a 
                        href={`/article/${article.id}`} 
                        onClick={(e) => handleNavigation(e, `/article/${article.id}`)}
                        className="view-button"
                      >
                        Lihat
                      </a>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="delete-button"
                      >
                        Hapus
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Paginasi */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <ul className="pagination">
                  <li className="pagination-item">
                    <button 
                      className={`pagination-link pagination-arrow ${currentPage === 1 ? 'disabled' : ''}`}
                      onClick={() => changePage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      &laquo;
                    </button>
                  </li>
                  
                  {getPageNumbers().map((page, index) => (
                    <li 
                      key={index} 
                      className={`pagination-item ${page === '...' ? 'hide-on-mobile' : ''}`}
                    >
                      {page === '...' ? (
                        <span className="pagination-ellipsis">&hellip;</span>
                      ) : (
                        <button
                          className={`pagination-link ${currentPage === page ? 'active' : ''}`}
                          onClick={() => changePage(page)}
                        >
                          {page}
                        </button>
                      )}
                    </li>
                  ))}
                  
                  <li className="pagination-item">
                    <button 
                      className={`pagination-link pagination-arrow ${currentPage === totalPages ? 'disabled' : ''}`}
                      onClick={() => changePage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      &raquo;
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}